'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'
import {
  Check,
  ChevronDown,
  X,
  BookOpen,
  Calendar,
  Send,
  Video,
  Sparkles,
  HeadphonesIcon,
  Users,
  Zap,
  ArrowRight,
  Lock,
  ShieldCheck,
  Rocket,
  TrendingUp,
  Trophy,
  Heart,
  Star,
} from 'lucide-react'

// ============================================================
// DATA
// ============================================================

const BENEFICIOS = [
  {
    icon: BookOpen,
    title: 'Acesso a TODOS os cursos',
    desc: '380+ aulas, 11 cursos completos. Do Vibecoding PRO ao APE.',
  },
  {
    icon: Calendar,
    title: 'Acesso a TODOS os eventos',
    desc: 'Eventos presenciais e online inclusos enquanto for assinante.',
  },
  {
    icon: Send,
    title: 'Grupo exclusivo no Telegram',
    desc: 'Networking direto com experts que estão construindo.',
  },
  {
    icon: Video,
    title: 'Prioridade em eventos ao vivo',
    desc: 'Cada encontro é um evento online. Suas dúvidas são respondidas antes nos eventos e no grupo.',
  },
  {
    icon: Sparkles,
    title: 'Conteúdo exclusivo',
    desc: 'Bastidores, cases reais, estratégias e materiais só para membros.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Suporte prioritário',
    desc: 'Suas dúvidas respondidas primeiro. Canal direto com a equipe.',
  },
]

const COMO_FUNCIONA = [
  {
    step: '01',
    title: 'Assine',
    desc: 'Escolha seu plano e finalize o pagamento pelo checkout seguro.',
    icon: Zap,
  },
  {
    step: '02',
    title: 'Vincule seu Telegram',
    desc: 'O bot envia o link de acesso ao grupo automaticamente.',
    icon: Send,
  },
  {
    step: '03',
    title: 'Entre no grupo',
    desc: 'Acesso imediato a tudo: cursos, eventos ao vivo, grupo e networking.',
    icon: Rocket,
  },
]

const PARA_QUEM = [
  {
    titulo: 'Experts construindo sua esteira',
    desc: 'Que querem acesso total a cursos, eventos e comunidade sem comprar um por um.',
  },
  {
    titulo: 'Consultores e mentores',
    desc: 'Que precisam de networking e troca direta com outros experts que estão construindo.',
  },
  {
    titulo: 'Infoprodutores em crescimento',
    desc: 'Que querem acompanhar bastidores reais e aplicar estratégias testadas.',
  },
  {
    titulo: 'Profissionais em transição',
    desc: 'Que estão migrando para o digital e precisam de suporte próximo e conteúdo atualizado.',
  },
  {
    titulo: 'Alunos do ecossistema eBuz',
    desc: 'Que já compraram cursos avulsos e querem consolidar tudo em um só lugar.',
  },
  {
    titulo: 'Empreendedores digitais',
    desc: 'Que entendem o valor de uma comunidade fechada de alta qualidade.',
  },
]

const COMPARATIVO = [
  { beneficio: 'Cursos', sem: 'Compra avulso (R$ 49 a R$ 4.997 cada)', com: 'Todos inclusos' },
  { beneficio: 'Eventos online', sem: 'Paga por evento (R$ 247,90+)', com: 'Todos inclusos' },
  { beneficio: 'Networking', sem: 'Nenhum', com: 'Grupo exclusivo' },
  { beneficio: 'Tirar dúvidas', sem: 'Fila comum', com: 'Prioridade nos eventos' },
  { beneficio: 'Suporte', sem: 'Padrão', com: 'Prioritário' },
]

const CHECKLIST = [
  'Acesso a todos os 11 cursos (380+ aulas)',
  'Entrada em todos os eventos presenciais e online',
  'Grupo exclusivo no Telegram',
  'Prioridade para tirar dúvidas nos eventos ao vivo',
  'Conteúdo exclusivo de bastidores',
  'Suporte prioritário',
  'Novos cursos inclusos automaticamente',
  'Parcelamento em até 6x no cartão',
]

const FAQ_ITEMS = [
  {
    q: 'Como funciona o acesso ao Telegram?',
    a: 'Após o pagamento, o bot envia um link exclusivo pessoal. Você clica e entra no grupo automaticamente. O link expira em 24 horas e só pode ser usado por você.',
  },
  {
    q: 'E se eu cancelar ou não renovar?',
    a: 'Ao final do período de 6 meses, se você não renovar, o bot remove seu acesso ao grupo e aos cursos. Seu histórico fica guardado para quando quiser voltar.',
  },
  {
    q: 'Tenho acesso a todos os cursos mesmo?',
    a: 'Sim. Todos os 11 cursos, 380+ aulas. Enquanto sua assinatura estiver ativa, você acessa tudo. Novos cursos lançados também entram automaticamente.',
  },
  {
    q: 'Os eventos presenciais estão inclusos?',
    a: 'Sim. Entrada gratuita em todos os eventos presenciais e online enquanto for assinante, sem custo adicional.',
  },
  {
    q: 'Posso parcelar?',
    a: 'Sim. O plano semestral pode ser pago em até 6x no cartão, ou à vista via Pix ou boleto com o mesmo valor.',
  },
  {
    q: 'Qual a diferença entre ser assinante e comprar cursos avulsos?',
    a: 'Comprando avulso você paga de R$ 49 a R$ 4.997 por cada curso. Como assinante, tem acesso a todos eles + todos os eventos ao vivo + grupo exclusivo por R$ 397/semestre. Mais barato a partir do 2º curso que você consumiria.',
  },
  {
    q: 'O que é o preço Founding Member?',
    a: 'É o preço dos primeiros assinantes, travado por tempo limitado. De R$ 497 por R$ 397 a cada 6 meses. Se você entrar agora, mantém esse preço nas renovações enquanto for assinante contínuo.',
  },
  {
    q: 'Já sou aluno de algum curso. Como fica?',
    a: 'Você continua com acesso ao curso que comprou. Ao virar assinante da comunidade, ganha acesso a tudo mais e entra no grupo exclusivo e nos eventos ao vivo com prioridade para tirar dúvidas.',
  },
]

// ============================================================
// FAQ Accordion
// ============================================================

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="space-y-3">
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          className="bg-white border border-stone-200/80 rounded-2xl overflow-hidden shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full px-5 sm:px-6 py-4 flex items-center justify-between text-left cursor-pointer"
          >
            <span className="text-sm font-semibold text-stone-900 pr-4">{item.q}</span>
            <ChevronDown
              className={`w-4 h-4 text-stone-400 shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
            />
          </button>
          {openIndex === i && (
            <div className="px-5 sm:px-6 pb-4">
              <p className="text-sm text-stone-500 leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================================
// BENEFIT CARD
// ============================================================

function BenefitCard({ item }: { item: typeof BENEFICIOS[number] }) {
  const Icon = item.icon
  return (
    <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-xl bg-[#006b5f]/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#006b5f]" />
      </div>
      <h4 className="text-base font-bold text-stone-900 mb-2">{item.title}</h4>
      <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
    </div>
  )
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function ComunidadePage() {
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  return (
    <div className="min-h-screen bg-[#fff8f5] text-stone-900 font-sans antialiased">

      {/* ====== NAV ====== */}
      <nav className="fixed w-full z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <Link href="/">
            <Image src="/logo.png" alt="eBuz" width={140} height={40} className="h-8 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#incluso" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">O que tem</a>
            <a href="#como-funciona" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">Como funciona</a>
            <a href="#preco" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">Preço</a>
            <a href="#faq" className="text-sm text-stone-600 hover:text-[#006b5f] transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/login" className="hidden sm:inline text-sm text-stone-600 hover:text-stone-900 transition-colors">
              Entrar
            </Link>
            <button
              onClick={() => setShowCheckout(true)}
              className="px-5 py-2 text-sm font-semibold bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg transition-colors cursor-pointer"
            >
              Quero Entrar
            </button>
          </div>
        </div>
      </nav>

      {/* ====== HERO ====== */}
      <header className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#006b5f]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-full px-4 py-1.5 mb-6">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-stone-600">Assinatura semestral - Founding Member</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Comunidade eBuz. O grupo{' '}
            <span className="bg-gradient-to-r from-[#006b5f] to-emerald-500 bg-clip-text text-transparent">
              exclusivo dos experts que constroem
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Acesso a todos os cursos, todos os eventos, networking com experts e acompanhamento direto com Jonathan.
            Tudo em um só lugar, em uma só assinatura.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <button
              onClick={() => setShowCheckout(true)}
              className="w-full sm:w-auto px-8 py-4 bg-[#006b5f] hover:bg-[#005a50] text-white font-bold rounded-xl shadow-lg shadow-[#006b5f]/20 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Entrar na Comunidade por R$ 397
            </button>
            <a
              href="#incluso"
              className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-stone-50 text-stone-700 font-semibold rounded-xl border border-stone-200 transition-colors text-center"
            >
              Ver o que está incluso
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-stone-500">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Acesso imediato
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Até 6x no cartão
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" /> Sem fidelidade
            </span>
          </div>
        </div>
      </header>

      <main>

        {/* ====== DOIS MUNDOS (WRAPPER CARDS) ====== */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Conhecimento e comunidade. Juntos.
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Não é só mais um curso. É o ecossistema completo de experts que estão construindo negócios digitais de verdade.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Box 1: Conhecimento */}
              <div className="bg-gradient-to-br from-[#006b5f]/5 to-emerald-50 rounded-3xl border border-[#006b5f]/10 p-8">
                <div className="inline-flex items-center gap-2 bg-[#006b5f]/10 rounded-full px-3 py-1 mb-4">
                  <BookOpen className="w-3.5 h-3.5 text-[#006b5f]" />
                  <span className="text-xs font-bold text-[#006b5f]">CONHECIMENTO</span>
                </div>
                <h3 className="text-xl font-black text-stone-900 mb-2">Todos os cursos. 380+ aulas.</h3>
                <p className="text-sm text-stone-500 mb-6">Do Vibecoding PRO ao APE, tudo liberado</p>
                <ul className="space-y-2">
                  {['Vibecoding PRO (44 aulas)', 'Arquitetura de Produtos Educacionais (67 aulas)', 'Pré-Vibecoding (30 aulas)', 'Funil Evergreen - Fundamentos + Prática (72 aulas)', 'SEO/GEO, Low Ticket, Setup Copy e mais'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-stone-700">
                      <Check className="w-4 h-4 text-[#006b5f] shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Box 2: Comunidade */}
              <div className="bg-gradient-to-br from-amber-500/5 to-orange-50 rounded-3xl border border-amber-500/10 p-8">
                <div className="inline-flex items-center gap-2 bg-amber-500/10 rounded-full px-3 py-1 mb-4">
                  <Heart className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-bold text-amber-600">COMUNIDADE</span>
                </div>
                <h3 className="text-xl font-black text-stone-900 mb-2">Networking + acompanhamento</h3>
                <p className="text-sm text-stone-500 mb-6">O que nenhum curso avulso entrega</p>
                <ul className="space-y-2">
                  {['Grupo exclusivo no Telegram', 'Prioridade para tirar dúvidas nos eventos ao vivo', 'Todos os eventos presenciais e online inclusos', 'Suporte prioritário da equipe', 'Bastidores e cases reais'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-stone-700">
                      <Check className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ====== BENEFÍCIOS ====== */}
        <section id="incluso" className="py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                O que está incluso
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Tudo o que você precisa para construir sua esteira de produtos educacionais, em um só lugar.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {BENEFICIOS.map((item) => (
                <BenefitCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* ====== COMO FUNCIONA ====== */}
        <section id="como-funciona" className="py-20 sm:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Como funciona
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                Do checkout à entrada no grupo em 3 passos simples.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {COMO_FUNCIONA.map((item) => {
                const Icon = item.icon
                return (
                  <div key={item.step} className="bg-stone-50 rounded-2xl border border-stone-100 p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-[#006b5f] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#006b5f]/20">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-xs font-bold text-[#006b5f] mb-2">{item.step}</p>
                    <h4 className="text-base font-bold text-stone-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ====== NÚMEROS ====== */}
        <section className="py-20 sm:py-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Em números
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { value: '380+', label: 'aulas liberadas', icon: BookOpen },
                { value: '11', label: 'cursos inclusos', icon: Star },
                { value: 'Todos', label: 'eventos inclusos', icon: Calendar },
                { value: 'Prioridade', label: 'para tirar dúvidas', icon: Video },
              ].map((item, i) => {
                const Icon = item.icon
                return (
                  <div key={i} className="bg-white rounded-2xl border border-stone-200/80 p-6 text-center shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-[#006b5f]/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-5 h-5 text-[#006b5f]" />
                    </div>
                    <p className="text-3xl sm:text-4xl font-black text-stone-900 mb-1">{item.value}</p>
                    <p className="text-xs text-stone-500">{item.label}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ====== COMPARATIVO ====== */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Sem comunidade vs. Com comunidade
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto">
                A diferença prática no dia a dia de quem constrói.
              </p>
            </div>

            <div className="bg-stone-50 rounded-3xl border border-stone-200/80 overflow-hidden">
              <div className="grid grid-cols-3 text-sm font-bold border-b border-stone-200/80">
                <div className="p-5 text-stone-900">Benefício</div>
                <div className="p-5 text-stone-400 border-l border-stone-200/80">Sem</div>
                <div className="p-5 text-[#006b5f] border-l border-stone-200/80 bg-[#006b5f]/5">Com</div>
              </div>
              {COMPARATIVO.map((row, i) => (
                <div key={i} className={`grid grid-cols-3 text-sm ${i !== COMPARATIVO.length - 1 ? 'border-b border-stone-200/50' : ''}`}>
                  <div className="p-5 font-semibold text-stone-900">{row.beneficio}</div>
                  <div className="p-5 border-l border-stone-200/50 flex items-center gap-2 text-stone-500">
                    <X className="w-4 h-4 text-red-400 shrink-0" />
                    <span>{row.sem}</span>
                  </div>
                  <div className="p-5 border-l border-stone-200/50 flex items-center gap-2 text-[#006b5f] font-medium bg-[#006b5f]/5">
                    <Check className="w-4 h-4 shrink-0" />
                    <span>{row.com}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== PARA QUEM É ====== */}
        <section className="py-20 sm:py-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Para quem é a Comunidade eBuz?
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PARA_QUEM.map((item) => (
                <div key={item.titulo} className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-[#006b5f]/10 flex items-center justify-center mb-4">
                    <Users className="w-4 h-4 text-[#006b5f]" />
                  </div>
                  <h4 className="text-sm font-bold text-stone-900 mb-2">{item.titulo}</h4>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== PRICING ====== */}
        <section id="preco" className="py-20 sm:py-28 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Uma assinatura. Tudo incluso.
              </h2>
              <p className="text-stone-500">
                Semestral, parcelável em até 6x. Sem fidelidade.
              </p>
            </div>

            <div className="relative bg-gradient-to-br from-[#006b5f] to-emerald-600 rounded-3xl p-8 sm:p-10 text-white shadow-xl shadow-[#006b5f]/20">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="inline-block px-5 py-1.5 rounded-full bg-amber-500 text-stone-900 text-xs font-bold uppercase tracking-wide shadow-lg">
                  Founding Member
                </span>
              </div>

              <div className="text-center mb-8 mt-2">
                <p className="text-emerald-200 text-sm font-semibold mb-2">COMUNIDADE eBUZ</p>
                <h3 className="text-2xl font-black mb-1">Assinatura Semestral Completa</h3>
                <p className="text-emerald-100 text-sm">Todos os cursos, eventos ao vivo e grupo exclusivo</p>
              </div>

              <div className="flex items-end justify-center gap-2 mb-1">
                <span className="text-sm text-emerald-200 line-through">R$ 497</span>
                <span className="text-5xl font-black">R$ 397</span>
              </div>
              <p className="text-center text-emerald-100 text-sm mb-8">a cada 6 meses - ou 6x de R$ 66,17</p>

              <ul className="grid sm:grid-cols-2 gap-3 mb-8 max-w-md mx-auto">
                {CHECKLIST.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-emerald-50">
                    <Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="text-center">
                <button
                  onClick={() => setShowCheckout(true)}
                  className="w-full sm:w-auto px-10 py-4 bg-white text-[#006b5f] font-bold rounded-xl hover:bg-emerald-50 transition-colors text-lg shadow-lg cursor-pointer"
                >
                  Entrar na Comunidade
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </button>
                <p className="text-xs text-emerald-200 mt-3 flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> Pagamento seguro via Pix, cartão ou boleto
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-stone-400">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Sem fidelidade - cancele quando quiser
              </span>
              <span className="flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4" /> Preço travado nas renovações
              </span>
            </div>
          </div>
        </section>

        {/* ====== FAQ ====== */}
        <section id="faq" className="py-20 sm:py-28">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
                Perguntas frequentes
              </h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* ====== FOOTER CTA ====== */}
        <section className="py-20 sm:py-28 bg-stone-900 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Seu conhecimento merece uma esteira.
              <br />
              Sua esteira merece uma comunidade.
            </h2>
            <p className="text-stone-400 max-w-xl mx-auto mb-8">
              O lugar onde experts de verdade constroem juntos, trocam o que funciona e não perdem tempo com fofoca.
            </p>
            <button
              onClick={() => setShowCheckout(true)}
              className="px-10 py-4 bg-[#006b5f] hover:bg-[#005a50] text-white font-bold rounded-xl shadow-lg shadow-[#006b5f]/30 hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              Entrar na Comunidade por R$ 397
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* ====== CHECKOUT MODAL ====== */}
      {showCheckout && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCheckout(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-stone-900">Finalizar assinatura</h3>
                <p className="text-sm text-stone-500">Comunidade eBuz - Semestral - R$ 397</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-stone-400 hover:text-stone-600 p-1 cursor-pointer">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                channelSlug="comunidade"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?ref=comunidade&payment=' + paymentId)
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
